import { Component } from '@angular/core';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';
import { PRODUCT_HEROES } from '../product-hero.data';
import { ProductShots, Shot } from '../product-shots';

// Promo landing page for the Color Picker Chrome extension, served at
// /plugins/chrome/color-picker. SEO is applied by the NavigationInterceptor from
// navigation.data.ts (client) and scripts/seo-route-meta.js (server). The copy
// describes only what the extension actually does: the same panel as the
// Codigrate Color Picker for macOS (conversions, CSS, accessibility, channels,
// harmony, ramps, temperature, color vision), recents, open-in-Codigrate, and
// the All In One theme match.
@Component( {
  selector: 'app-chrome-color-picker-page',
  templateUrl: './chrome-color-picker-page.component.html',
  styleUrls: [ './chrome-color-picker-page.component.scss', '../macos-tidy-desktop-page/macos-tidy-desktop-page.component.scss', '../product-page.scss' ],
} )
export class ChromeColorPickerPageComponent extends ProductShots {

  readonly storeUrl = 'https://chromewebstore.google.com/detail/kemglmedckibbmnfmeppkgconcpckepm';
  // The product on its stage at the top of the page (product-hero.data.ts), and the facts as
  // chips under the pitch.
  readonly hero = PRODUCT_HEROES[ 'chrome-color-picker' ];
  readonly facts = [ 'Free', 'No Sign In, No Tracking', 'Colors Stay On Your Device' ];


  readonly features = [
    {
      title: 'Eyedrop Anywhere',
      description: 'Sample any pixel on your screen. A web page, an image, a video, a design tool, anything you can see.',
    },
    {
      title: 'Every Color Space',
      description: 'Hex, RGB, HSL, HSV, HWB, CMYK, OKLCH, OKLab, CIELAB, CIELCH, XYZ, Yxy, Hunter Lab and the nearest RAL code, each one click to copy. Plus copy-ready CSS rules.',
    },
    {
      title: 'Contrast Checks',
      description: 'WCAG 2 ratio and APCA of the color as text on white, black and its complement, with AA and AAA verdicts.',
    },
    {
      title: 'Harmony and Ramps',
      description: 'Complementary, triadic, tetradic and more, ten-step tints, shades and tones, warmer and cooler neighbours, and color vision deficiency simulations.',
    },
    {
      title: 'Recent Colors',
      description: 'Your last picks stay in the popup, so the shades you are working with are always one click away.',
    },
    {
      title: 'Private and Free',
      description: 'No sign in, no clutter, no tracking. Your colors never leave your device.',
    },
  ];

  // The first three things it does under the product's picture, the rest further down (the
  // Apps & Plugins layout, ../product-page.scss).
  readonly highlights = this.features.slice( 0, 3 );
  readonly more = this.features.slice( 3 );

  // The store pictures (2026-10-09, in All In One Themes' store style), under Screenshots; each
  // opens in the lightbox at full size.
  readonly shots: Shot[] = [
    {
      name: 'Grab Any Color',
      src: './assets/product-lines/tools/chrome-color-picker/screenshot-grab-any-color.webp',
      large: './assets/product-lines/tools/chrome-color-picker/screenshot-grab-any-color-large.webp',
      alt: 'Grab any color, right in Chrome: the Color Picker popup with a picked indigo in every color space',
    },
    {
      name: 'Any Page',
      src: './assets/product-lines/tools/chrome-color-picker/screenshot-any-page.webp',
      large: './assets/product-lines/tools/chrome-color-picker/screenshot-any-page-large.webp',
      alt: 'Pick from any page: the popup open in Chrome over a photo of a tangerine, with its orange picked',
    },
    {
      name: 'Readable Or Not',
      src: './assets/product-lines/tools/chrome-color-picker/screenshot-readable.webp',
      large: './assets/product-lines/tools/chrome-color-picker/screenshot-readable-large.webp',
      alt: 'The popup\'s contrast checks for an orange: WCAG 2 ratios and APCA against white, black and its complement, and its color channels',
    },
    {
      name: 'Harmonies',
      src: './assets/product-lines/tools/chrome-color-picker/screenshot-harmonies.webp',
      large: './assets/product-lines/tools/chrome-color-picker/screenshot-harmonies-large.webp',
      alt: 'The popup\'s harmonies for a teal: complementary, analogous, triadic and more, with its tints and shades',
    },
  ];

  constructor( private analyticsService: AnalyticsService ) {
    super();
  }

  onInstall(): void {
    this.analyticsService.logEvent( 'Extension Install Clicked', { extension: 'color-picker' } );
  }
}
